import { useEffect, useMemo, useState } from 'react';
import { Tag } from '../../../../../../common/tags/tagTypes';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';
import { getElementId, getTags } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { useDispatch, useSelector } from 'react-redux';
import { getSelectedTaskIdSelector } from '../../../selector/selectionToolsSelector';
import { List } from 'immutable';
import { getElement } from '../../../../../element/selectors/elementSelector';
import { fetchTagsFromDb, updateTag } from '../../../../../element/tags/tagsService';
import { addTagToElements, removeTagFromElements } from '../../../../../element/tags/tagsActions';
import { isEmpty } from 'lodash';

const useTagContentState = (
    currentBoardId: string,
    userRootPath: Array<ImMNElement>,
    selectedElements: Immutable.List<ImMNElement>,
) => {
    const [searchString, setSearchString] = useState<string>('');
    const [currentContextTags, setCurrentContextTags] = useState<Array<Tag>>([]);
    // TODO-tags once tags data added to redux, set tags to all tags when there is a search string
    const tags = currentContextTags;

    const elementIds = selectedElements.map(getElementId).toArray();
    const selectedTaskListId = useSelector(getSelectedTaskIdSelector);
    const focusElementIds = selectedTaskListId ? [selectedTaskListId] : elementIds;
    const elements: List<ImMNElement> = useSelector((state) => {
        return List(focusElementIds.map((elementId) => getElement(state, { elementId })));
    });

    const dispatch = useDispatch();
    const handleAddTagToElement = (tag: Tag) => {
        if (!tag.rootBoardIds?.includes(currentBoardId)) {
            updateTag(tag._id, { rootBoardIds: [...tag.rootBoardIds, currentBoardId] });
        }

        dispatch(addTagToElements(elements, tag._id));
    };
    const handleRemoveTagFromElement = (tag: Tag) => dispatch(removeTagFromElements(elements, tag._id));
    const handleClickSelectedTag = (tag: Tag) => {
        const isTagSelectedForEveryElement = elements.every((element) => (getTags(element) || []).includes(tag._id));
        const updateFunction = isTagSelectedForEveryElement ? handleRemoveTagFromElement : handleAddTagToElement;

        updateFunction(tag);
    };

    const fetchCurrentContextTags = () => {
        const ancestors = userRootPath?.map((element) => getElementId(element));
        fetchTagsFromDb({ boardIds: ancestors || [currentBoardId] }).then(setCurrentContextTags);
    };

    const filteredTags = useMemo(() => {
        if (isEmpty(searchString)) return tags;

        const value = searchString!.toLowerCase();
        return tags.filter((tag) => tag.name.toLowerCase().includes(value));
    }, [tags, searchString]);

    const elementTagIds = elements.flatMap((element) => getTags(element) || []);
    const [selectedTags, unselectedTags] = filteredTags.reduce(
        (accumulator: [Tag[], Tag[]], tag) => {
            elementTagIds?.includes(tag._id) ? accumulator[0].push(tag) : accumulator[1].push(tag);
            return accumulator;
        },
        [[], []],
    );

    useEffect(() => {
        fetchCurrentContextTags();
    }, []);

    return {
        searchString,
        setSearchString,
        selectedTags,
        unselectedTags,
        handleClickSelectedTag,
        handleAddTagToElement,
        fetchCurrentContextTags,
    };
};

export default useTagContentState;
